import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "@/server/ai-gateway";
import { generateEmbedding, EmbeddingError } from "@/server/lib/embeddings";
import { enforceQuota, incrementUsage, QuotaExceededError } from "@/server/lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type RAGAnswer = {
  situation: string;
  task: string;
  action: string;
  result: string;
  fullAnswer: string;
  sourceRole?: string;
  sources: Array<{
    source_type: string;
    source_ref: string | null;
    similarity: number;
    excerpt: string;
  }>;
};

const STAR_TONE_TOOL = {
  name: "save_star_answer",
  description:
    "Answer a job application or interview question using the STAR method (Situation, Task, Action, Result), grounded ONLY in the provided candidate context. Match the requested tone. Never invent facts not present in the context. CRITICAL: When the candidate has many years of experience and multiple roles in the context, deliberately VARY which role/project you draw from across different questions — do not keep returning to the same single project. Pick the role/project whose details best match THIS specific question.",
  parameters: {
    type: "object",
    properties: {
      situation: { type: "string", description: "Set the scene with real context from the candidate's history. Name the specific company/role/project this story comes from. 2-3 sentences." },
      task: { type: "string", description: "What needed to be accomplished. 1-2 sentences." },
      action: { type: "string", description: "Specific actions YOU (the candidate) took. 3-5 sentences." },
      result: { type: "string", description: "Concrete, quantified outcome where possible. 2-3 sentences." },
      fullAnswer: {
        type: "string",
        description: "A polished paragraph version combining all four sections, ready to paste into an application form. Match the requested tone.",
      },
      sourceRole: {
        type: "string",
        description: "The single company/role/project this answer is primarily drawn from (e.g. 'Eightfold AI — Design Director'). Used to track diversity across answers.",
      },
    },
    required: ["situation", "task", "action", "result", "fullAnswer", "sourceRole"],
    additionalProperties: false,
  },
};

const TONE_GUIDES: Record<string, string> = {
  corporate: "Formal, polished, structured. Avoid slang. Use precise business language.",
  startup: "Conversational, direct, first-person. Confident and concise. Show ownership.",
  creative: "Distinctive voice. Vivid concrete details. Show personality without losing substance.",
};

/**
 * Diversify a list of retrieved chunks so a single noisy "raw" source
 * (e.g. linkedin/raw with 30 chunks) cannot monopolise the context window.
 * Strategy: round-robin across (source_type, source_ref) buckets in order
 * of best similarity per bucket, so multiple roles/projects make it in.
 */
function diversifyChunks<T extends { source_type: string; source_ref: string | null; similarity: number }>(
  chunks: T[],
  targetCount: number,
  maxPerBucket: number,
): T[] {
  const buckets = new Map<string, T[]>();
  for (const c of chunks) {
    const key = `${c.source_type}::${c.source_ref ?? ""}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(c);
  }
  for (const arr of buckets.values()) arr.sort((a, b) => b.similarity - a.similarity);
  const orderedKeys = Array.from(buckets.keys()).sort(
    (a, b) => (buckets.get(b)![0]?.similarity ?? 0) - (buckets.get(a)![0]?.similarity ?? 0),
  );
  const out: T[] = [];
  for (let pass = 0; pass < maxPerBucket && out.length < targetCount; pass++) {
    for (const key of orderedKeys) {
      if (out.length >= targetCount) break;
      const next = buckets.get(key)![pass];
      if (next) out.push(next);
    }
  }
  return out;
}

export const answerWithRAG = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      question: string;
      tone?: "corporate" | "startup" | "creative";
      topK?: number;
      avoidRoles?: string[];
    }) => {
      if (!input?.question || input.question.trim().length < 10) {
        throw new Error("question is required (min 10 chars)");
      }
      if (input.question.length > 2000) {
        throw new Error("question too long (max 2000 chars)");
      }
      const tone = input.tone ?? "startup";
      if (!["corporate", "startup", "creative"].includes(tone)) {
        throw new Error("invalid tone");
      }
      const topK = Math.min(Math.max(input.topK ?? 8, 3), 15);
      const avoidRoles = Array.isArray(input.avoidRoles)
        ? input.avoidRoles.filter((s) => typeof s === "string" && s.trim().length > 0).slice(0, 10)
        : [];
      return { question: input.question.trim(), tone, topK, avoidRoles };
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    try {
      await enforceQuota(supabase, userId, CAPABILITIES.qa_answer);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    // 1. Embed the question
    let qEmbedding: number[];
    try {
      qEmbedding = await generateEmbedding(data.question);
    } catch (e) {
      if (e instanceof EmbeddingError) {
        throw new Response(e.message, { status: e.status });
      }
      throw e;
    }

    // 2. Over-retrieve so diversification has something to work with.
    // Many candidates have one source (e.g. linkedin/raw) split into 30+ chunks
    // that would otherwise dominate top-K and force every answer to draw from
    // the same handful of sentences.
    const overFetch = Math.max(data.topK * 4, 24);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: matches, error: matchErr } = await supabaseAdmin.rpc("match_candidate_chunks", {
      _user_id: userId,
      query_embedding: `[${qEmbedding.join(",")}]`,
      match_count: overFetch,
    });

    if (matchErr) {
      throw new Response(`Retrieval failed: ${matchErr.message}`, { status: 500 });
    }

    const allChunks = (matches as Array<{
      id: string;
      source_type: string;
      source_ref: string | null;
      content: string;
      similarity: number;
    }> | null) ?? [];

    if (allChunks.length === 0) {
      throw new Response(
        "No indexed history found. Click 'Reindex my history' on your profile first.",
        { status: 400 },
      );
    }

    // 3. Diversify: round-robin across (source_type, source_ref) buckets so
    // multiple roles/projects are represented. Cap any single bucket at ~25%
    // of slots so noisy raw sources can't take over.
    const maxPerBucket = Math.max(2, Math.ceil(data.topK / 4));
    const chunks = diversifyChunks(allChunks, data.topK, maxPerBucket);

    // 4. Build retrieval context, grouped by source so the model sees
    // distinct roles/projects rather than a soup of similar sentences.
    const grouped = new Map<string, typeof chunks>();
    for (const c of chunks) {
      const key = `${c.source_type}${c.source_ref ? ` — ${c.source_ref}` : ""}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(c);
    }
    const retrievalContext = Array.from(grouped.entries())
      .map(([key, group], gi) => {
        const body = group
          .map((c, i) => `  [${gi + 1}.${i + 1}] (sim ${c.similarity.toFixed(2)})\n  ${c.content.replace(/\n/g, "\n  ")}`)
          .join("\n\n");
        return `=== Source ${gi + 1}: ${key} ===\n${body}`;
      })
      .join("\n\n");

    const distinctSources = Array.from(grouped.keys());
    const avoidLine =
      data.avoidRoles.length > 0
        ? `\n\nIMPORTANT: The candidate has already used the following role(s) in earlier answers in this session: ${data.avoidRoles.join("; ")}. Prefer drawing this answer from a DIFFERENT role/project in the context above so the candidate has variety. Only re-use one of those roles if NO other source in the context fits the question.`
        : "";

    // 5. Generate STAR answer
    let result: Omit<RAGAnswer, "sources">;
    try {
      result = await callAIStructured<Omit<RAGAnswer, "sources">>({
        messages: [
          {
            role: "system",
            content: `You are a career coach helping a candidate answer an application question using the STAR method. ${TONE_GUIDES[data.tone]} Use ONLY the provided candidate context. If context is insufficient, say so in the situation field rather than inventing. The candidate's full history is split across MULTIPLE distinct sources/roles below — pick the SINGLE source whose details best match this specific question, and ground the answer in that role's specifics. Do NOT default to whichever source has the most text.`,
          },
          {
            role: "user",
            content: `Question: ${data.question}\n\nCandidate context — ${distinctSources.length} distinct source(s) below. Choose the best-fit source for this question:\n\n${retrievalContext}${avoidLine}`,
          },
        ],
        tool: STAR_TONE_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError) {
        throw new Response(e.message, { status: e.status });
      }
      throw e;
    }

    const answer: RAGAnswer = {
      ...result,
      sources: chunks.map((c) => ({
        source_type: c.source_type,
        source_ref: c.source_ref,
        similarity: c.similarity,
        excerpt: c.content.slice(0, 220) + (c.content.length > 220 ? "…" : ""),
      })),
    };

    // 6. Persist
    await supabase.from("generated_materials").insert({
      user_id: userId,
      type: "qa",
      content: {
        question: data.question,
        tone: data.tone,
        ...answer,
      } as unknown as never,
    });

    await incrementUsage(userId, CAPABILITIES.qa_answer);

    return answer;
  });
